import React, { useEffect, useState, useMemo, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Avatar,
  IconButton,
  Breadcrumbs,
  Link,
  Paper,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Stack,
  CircularProgress,
} from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  Person as PersonIcon,
  Home as HomeIcon,
  People as PeopleIcon,
  Add as AddIcon,
  Person as PersonIcon2,
  Badge as BadgeIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import Input from "../../components/Input";
import { motion } from "framer-motion";
import { roleService } from "../../services/roles/roleService";
import { ClientMultiSelect, RoleResponse } from "../../types/api.types";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import PhoneIcon from "@mui/icons-material/Phone";
import { userService } from "../../services/user/userService";
import { useSnackbar } from "../../context/SnackbarContext";
import Dropdown from "../../components/Dropdown";
import CompanyMultiSelect from "../../components/CompanyMultiSelect";
import { clientService } from "../../services/client/clientService";
import { formatCuitMask, stripCuitDigits } from "../../utils/cuit";
interface UserFormData {
  email: string;
  cuit: string;
  telephone: string;
  name: string;
  profilePicture?: File;
  rolId: string;
  clientIds: string[];
}

const UserCreatePage = () => {
  const navigate = useNavigate();
  const [profileImage, setProfileImage] = useState<string | null>(null);

  const [isLoadingRoles, setIsLoadingRoles] = useState<boolean>(false);

  const [roles, setRoles] = useState<RoleResponse[]>([]);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [isLoadingClients, setIsLoadingClients] = useState<boolean>(false);

  const [clientsMultiSelect, setClientsMultiSelect] = useState<
    ClientMultiSelect[]
  >([]);

  const { showSnackbar } = useSnackbar();
  const clientRequest = useRef(0);
  const [clientResultIds, setClientResultIds] = useState<string[]>([]);
  const [clientTotal, setClientTotal] = useState(0);


  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<UserFormData>({
    defaultValues: {
      email: "",
      cuit: "",
      telephone: "",
      name: "",
      clientIds: [],
    },
  });

  const role = watch("rolId");

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        setProfileImage(result);
      };
      reader.readAsDataURL(file);
      setValue("profilePicture", file);
    }
  };

  const onSubmit = async (data: UserFormData) => {
    try {
      setIsLoading(true);
      await userService.createUser({
        ...data,
        password: "123456",
        cuit: formatCuitMask(data.cuit),
      });
      navigate("/users");
      showSnackbar("Usuario creado correctamente", "success", 3000);
    } catch (error: any) {
      showSnackbar(
        error.response?.data?.message || "Error al crear usuario",
        "error",
        4000,
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/users");
  };

  const getRoles = async () => {
    setIsLoadingRoles(true);
    const response = await roleService.getRoles();
    setRoles(response);
    setIsLoadingRoles(false);
  };

  const getClientsMultiSelect = async (search: string, page = 0, rowPerPage = 20) => {
    const request = ++clientRequest.current;
    setIsLoadingClients(true);
    try {
      const response = await clientService.getClientsMultiSelectPage(search, page, rowPerPage);
      if (request !== clientRequest.current) return;
      const list = response.clients ?? [];
      setClientTotal(response.total ?? 0);
      setClientResultIds(list.map((client) => client.clientID));
      setClientsMultiSelect((prev) => Array.from(new Map(
        [...prev, ...list].map((client) => [client.clientID, client]),
      ).values()));
    } catch (error: any) {
      if (request !== clientRequest.current) return;
      showSnackbar(error.response?.data?.message || "Error al cargar empresas", "error", 4000);
    } finally {
      if (request === clientRequest.current) setIsLoadingClients(false);
    }
  };

  useEffect(() => {
    getRoles();
  }, []);

  // Memoize client options to prevent recreation on every render
  const clientOptions = useMemo(() => {
    return clientsMultiSelect
      .filter((client) => client.clientID) // Filter out invalid entries
      .map((client) => ({
        id: client.clientID,
        comercialName: client.comercialName?.trim() ?? "",
        legalName: client.legalName?.trim() ?? "",
        cuit: client.cuit ?? "",
        label: `${client.comercialName} - ${client.legalName}`,
      }));
  }, [clientsMultiSelect]);

  const selectedRoleName = useMemo(() => {
    let selectedRole = roles.find((x) => x.rolId === String(role));
    return selectedRole?.name ?? null;
  }, [roles, role]);

  if (isLoadingRoles) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Form Card */}
      <Card elevation={2}>
        <CardContent sx={{ p: 4 }}>
          {/* Breadcrumbs */}
          <Breadcrumbs sx={{ mb: 3 }}>
            <Link
              underline="hover"
              color="inherit"
              href="#"
              onClick={() => navigate("/home")}
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              Inicio
            </Link>
            <Link
              underline="hover"
              color="inherit"
              href="#"
              onClick={() => navigate("/users")}
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              Usuarios
            </Link>
            <Typography
              color="text.primary"
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              Crear Usuario
            </Typography>
          </Breadcrumbs>

          {/* Page Header */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h4" component="h1" gutterBottom>
              Crear Nuevo Usuario
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Complete la información del nuevo usuario del sistema
            </Typography>
          </Box>

          <form onSubmit={handleSubmit(onSubmit)}>
            <Box
              sx={{
                display: "flex",
                gap: 4,
                flexDirection: { xs: "column", md: "row" },
              }}
            >
              {/* Profile Picture Section */}
              {/* <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                  minWidth: { xs: "100%", md: "280px" },
                }}
              >
                <Typography variant="h6" gutterBottom>
                  Foto de Perfil
                </Typography>

                <Avatar
                  src={profileImage || undefined}
                  sx={{
                    width: 120,
                    height: 120,
                    bgcolor: "primary.main",
                    fontSize: "3rem",
                  }}
                >
                  <PersonIcon fontSize="inherit" />
                </Avatar>

                <input
                  accept="image/*"
                  style={{ display: "none" }}
                  id="profile-image-upload"
                  type="file"
                  onChange={handleImageUpload}
                />

                <label htmlFor="profile-image-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CloudUploadIcon />}
                    sx={{ width: "100%" }}
                  >
                    Subir Imagen
                  </Button>
                </label>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  align="center"
                >
                  JPG, PNG o GIF. Máximo 5MB
                </Typography>
              </Box> */}

              {/* Form Fields */}
              <Box sx={{ flex: 1 }}>
                <Stack spacing={3}>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      flexDirection: { xs: "column", sm: "row" },
                    }}
                  >
                    {/* Username */}
                    <Box sx={{ flex: 1 }}>
                      <Controller
                        name="email"
                        control={control}
                        rules={{
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: "Debe ser un email válido",
                          },
                          required: "El email es requerido",
                          minLength: {
                            value: 3,
                            message: "Mínimo 3 caracteres",
                          },
                        }}
                        render={({ field }) => (
                          <Input
                            label="Email"
                            name="email"
                            field={field}
                            required
                            error={errors.email?.message}
                            icon={<AlternateEmailIcon />}
                            sx={{ width: "100%" }}
                          />
                        )}
                      />
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      <Controller
                        name="cuit"
                        control={control}
                        rules={{
                          required: "El cuit es requerido",
                          minLength: {
                            value: 11,
                            message: "El CUIT debe tener 11 dígitos",
                          },
                          pattern: {
                            value: /^\d{11}$/,
                            message: "El CUIT debe contener solo 11 dígitos",
                          },
                        }}
                        render={({ field }) => (
                          <Input
                            label="Cuit"
                            name="cuit"
                            field={{
                              ...field,
                              value: formatCuitMask(field.value),
                              onChange: (
                                e: React.ChangeEvent<HTMLInputElement>,
                              ) => {
                                field.onChange(stripCuitDigits(e.target.value));
                              },
                            }}
                            required
                            error={errors.cuit?.message}
                            icon={<BadgeIcon />}
                            sx={{ width: "100%" }}
                            type="text"
                            inputProps={{
                              inputMode: "numeric",
                              autoComplete: "off",
                              maxLength: 13,
                            }}
                          />
                        )}
                      />
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      flexDirection: { xs: "column", sm: "row" },
                    }}
                  >
                    {/* Username */}
                    <Box sx={{ flex: 1 }}>
                      <Controller
                        name="telephone"
                        control={control}
                        rules={{
                          required: "El teléfono es requerido",
                          minLength: {
                            value: 3,
                            message: "Mínimo 3 caracteres",
                          },
                        }}
                        render={({ field }) => (
                          <Input
                            label="Teléfono"
                            name="telephone"
                            field={field}
                            required
                            error={errors.telephone?.message}
                            icon={<PhoneIcon />}
                            sx={{ width: "100%" }}
                          />
                        )}
                      />
                    </Box>

                    {/* Role */}
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5 }}
                      style={{ flex: 1 }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 0.5 }}
                      >
                        Rol
                      </Typography>
                      <Controller
                        name="rolId"
                        control={control}
                        rules={{ required: "El rol es requerido" }}
                        render={({ field }) => (
                          <Dropdown
                            field={field}
                            options={roles.map((role) => ({
                              id: role.rolId,
                              label: role.name,
                            }))}
                            label="Rol"
                            error={errors.rolId?.message}
                          />
                        )}
                      />
                    </motion.div>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      flexDirection: { xs: "column", sm: "row" },
                    }}
                  >
                    {/* First Name */}
                    <Box sx={{ flex: 1 }}>
                      <Controller
                        name="name"
                        control={control}
                        rules={{
                          required: "El nombre es requerido",
                          minLength: {
                            value: 2,
                            message: "Mínimo 2 caracteres",
                          },
                        }}
                        render={({ field }) => (
                          <Input
                            label="Nombre"
                            name="name"
                            field={field}
                            required
                            error={errors.name?.message}
                            icon={<PersonIcon2 />}
                            sx={{ width: "100%" }}
                          />
                        )}
                      />
                    </Box>


                  </Box>
                  <Box sx={{ minWidth: 0 }}> 
                      {selectedRoleName === "Client" && (
                        <Controller
                          name="clientIds"
                          control={control}
                          rules={{
                            validate: (value) => value?.length > 0 || "Debe seleccionar al menos una empresa",
                          }}
                          render={({ field }) => (
                            <CompanyMultiSelect
                              options={clientOptions}
                              resultIds={clientResultIds}
                              value={field.value ?? []}
                              onChange={field.onChange}
                              onBlur={field.onBlur}
                              inputRef={field.ref}
                              error={errors.clientIds?.message}
                              disabled={isLoading}
                              loading={isLoadingClients}
                              total={clientTotal}
                              onRequest={(search, page, rowPerPage) => {
                                void getClientsMultiSelect(search, page, rowPerPage);
                              }}
                            />
                          )}
                        />
                      )}
                    </Box>
                </Stack>
              </Box>
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* Action Buttons */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                mt: 3,
              }}
            >
              <Button
                variant="outlined"
                onClick={handleCancel}
                sx={{ minWidth: 120 }}
                disabled={isLoading}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="contained"
                sx={{ minWidth: 120 }}
                disabled={isLoading}
                loading={isLoading}
              >
                Crear Usuario
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default UserCreatePage;
